<template>
	<span class="expandable-text">
		<span ref="textElement" class="text" :class="{ expanded: isExpanded }">{{ text }}</span>
		<button
			v-if="isClamped || isExpanded"
			type="button"
			class="toggle"
			:aria-expanded="isExpanded"
			@click="isExpanded = !isExpanded"
		>
			{{ isExpanded ? $t("callToActions.showLess") : $t("callToActions.showMore") }}
		</button>
	</span>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from "vue"
import { useResizeObserver } from "@vueuse/core"

const props = defineProps<{
	text: string
}>()

const textElement = ref<HTMLElement | null>(null)
const isExpanded = ref(false)
const isClamped = ref(false)

function measure(): void {
	const element = textElement.value
	if (!element || isExpanded.value) return
	isClamped.value = element.scrollHeight > element.clientHeight
}

useResizeObserver(textElement, measure)

watch(
	() => props.text,
	async () => {
		isExpanded.value = false
		await nextTick()
		measure()
	}
)
</script>

<style scoped>
.expandable-text {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: var(--spacing-xxs);
	min-width: 0;

	.text {
		display: -webkit-box;
		overflow: hidden;
		overflow-wrap: anywhere;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;

		&.expanded {
			display: block;
			overflow: visible;
			-webkit-line-clamp: unset;
		}
	}

	.toggle {
		padding: 0;
		border: none;
		background: none;
		color: var(--primary-green);
		cursor: pointer;
		font: inherit;
		font-weight: var(--font-weight-semibold);

		&:hover {
			text-decoration: underline;
		}
	}
}
</style>
